import React, { ReactElement } from 'react';
import Box from '../../components/Box/Box';
import Icon from '../../components/Icon/Icon';
import { TextBodySmall } from '../../components/Text';
import getComponentClassNames from '../../utils/className/getComponentClassNames';
import type { TColor, TIcon } from '../../types/TStyleVariables';

export type TStarRatingProps = {
  label?: string;
  ariaLabel?: string;
  /** A value between 0 and 5. */
  rating: number;
};

type TStarRatingSettings = {
  color: TColor;
  count: number;
};

type TStarsProps = {
  count: number;
  name: TIcon;
} & TStarRatingSettings;

const totalStarsCount = 5;
const filledColor = 'text-brand-primary';
const emptyColor = 'text-secondary';

const classNames = getComponentClassNames(['StarRating'], ['label']);

function extractStarRatingInfo(rating: number) {
  const filledStarsCount = Math.floor(rating);
  const hasHalfStar = !Number.isInteger(rating);
  return { filledStarsCount, hasHalfStar };
}

function countEmptyStars(total: number, filledStarsCount: number, hasHalfStar: boolean): number {
  return total - filledStarsCount - Number(hasHalfStar);
}

function Stars({ count, ...rest }: TStarsProps): ReactElement[] {
  return Array.from(Array(count)).map((_, index) => <Icon key={String(index)} {...rest} />);
}

function StarRating({ label, ariaLabel, rating, ...rest }: TStarRatingProps): ReactElement {
  const { filledStarsCount, hasHalfStar } = extractStarRatingInfo(rating);
  const labelClassName = `${classNames.label} ${classNames.label}--m`;

  return (
    <Box as="span" name="StarRating" {...rest}>
      <Stars count={filledStarsCount} color={filledColor} name="saved" />
      {hasHalfStar && <Icon name="saved-half" color={filledColor} />}
      <Stars count={countEmptyStars(totalStarsCount, filledStarsCount, hasHalfStar)} color={emptyColor} name="save" />
      <TextBodySmall className={labelClassName} aria-label={ariaLabel}>
        {label}
      </TextBodySmall>
    </Box>
  );
}

export default StarRating;
